/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { getSharedDataObject, getValue } from '../../store/state/state-manager';
import AnnotationsHandler from '..';
import Constants from '../../constants';
import Utils from '../../utils/utils';
import AnnotationProcessor from '../processor';
import DatapageDependencyMap from '../../cascade/datapage-dependency-handler';
import { C11nEnv } from '../../interpreter/c11n-env';

interface Parameter {
  name: string;
  value: string;
}

interface DataSource {
  tableType: string;
  tableClass?: string;
  name?: string;
  propertyForDisplayText?: string;
  propertyForValue?: string;
  parameters?: Parameter[];
  mode?: string;
  records?: { key: string; value: string }[];
}

// Make MetaDataType properties optional
interface MetaDataType {
  classID?: string;
  type?: string;
  displayAs?: string;
  label?: string;
  datasource?: DataSource;
  localeReference?: string;
}

interface ComponentConfigType {
  value: string;
  label: string;
  placeholder: string;
  listType: string;
  datasource: string;
  testId: string;
  deferDatasource: string;
  datasourceMetadata: string;
}

function getResultsFromDataPage(
  c11nEnv: C11nEnv,
  parameterValueArray: string[],
  dataPageName: string,
  isSharedDataPage: boolean
) {
  let hashDigest = '';
  let dataPageWithHash = dataPageName;

  if (parameterValueArray.join(Constants.HASH_DELIMITER)) {
    hashDigest = Utils.getHashedString(parameterValueArray.join(Constants.HASH_DELIMITER));
    dataPageWithHash = dataPageName + Constants.UNDERSCORE_STRING + hashDigest;
  }
  if (isSharedDataPage) {
    const sharedDataObject = getSharedDataObject();
    const sharedData = sharedDataObject ? sharedDataObject[dataPageName]?.[dataPageWithHash] : {};
    return Array.isArray(sharedData?.pxResults) ? sharedData.pxResults : [];
  }
  const dataInContext = c11nEnv.getDataObject();
  const dataPagesObject = dataInContext?.[dataPageName] || {};
  const dataPage = dataPagesObject[dataPageWithHash] || {};
  return Array.isArray(dataPage?.pxResults) ? dataPage.pxResults : [];
}

function getResultsFromExpression(
  datasource: { propertyForDisplayText?: string; propertyForValue?: string; name?: string } | undefined,
  c11nEnv: C11nEnv
) {
  const propertyForDisplayText = AnnotationProcessor.getPropertyName(datasource?.propertyForDisplayText ?? '');
  const propertyForValue = AnnotationProcessor.getPropertyName(datasource?.propertyForValue ?? '');
  const results = c11nEnv.getContextDataValue(`.${Constants.SUMMARY_OF_EXPRESSIONS}.${datasource?.name}`) || [];
  return results?.map((val: { [key: string]: string }) => {
    return {
      key: val[propertyForValue] || val[`.${propertyForValue}`],
      value: val[propertyForDisplayText] || val[`.${propertyForDisplayText}`]
    };
  });
}

/**
 * This handler will be invoked if the property is configured as associated property.
 * If the property is configured with data page will resolve the value from the data object else
 * we resolve it from context_data.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
AnnotationsHandler.registerHandler('@ASSOCIATED', (value: string, c11nEnv: C11nEnv) => {
  let metaData: MetaDataType = {};
  const parameterValueArray: string[] = [];
  let propertyName = '';
  let parentPropertyName = '';
  const pageReference = c11nEnv.getPageReference();
  if (value.startsWith('.')) {
    propertyName = `${pageReference}.${value.substring(1)}`;
    const parentPropertySplitArray = value.split('.').filter((val) => !!val);
    if (parentPropertySplitArray.length > 1) {
      parentPropertySplitArray.pop();
      parentPropertyName = parentPropertySplitArray.join('.');
    }
    metaData = c11nEnv.getFieldMetadata(value.substring(1));
  } else {
    metaData = c11nEnv.getFieldMetadata(value);
    propertyName = value;
  }
  const tableType = metaData?.datasource?.tableType;
  const isSharedDataPage = metaData?.datasource?.mode === Constants.READONLY;

  const dataPageName = metaData?.datasource?.name || '';
  const componentConfig: ComponentConfigType = c11nEnv.getComponentConfig();
  if (componentConfig && componentConfig.deferDatasource) {
    componentConfig.datasourceMetadata = `@FIELD_META ${value}`;
    c11nEnv.setStateProps({
      datasourceMetadata: componentConfig.datasourceMetadata
    });
    if (tableType === Constants.DATA_PAGE) {
      return null;
    }
  }
  if (tableType === Constants.DECISION || tableType === Constants.RULE_REFERENCE) {
    return getResultsFromExpression(metaData?.datasource, c11nEnv);
  }
  if (tableType !== Constants.DATA_PAGE) {
    const l11nService = c11nEnv.getLocalizationService(metaData?.localeReference);
    return metaData?.datasource?.records?.map((record: { key: string; value: string }) => {
      return {
        key: record.key,
        value: l11nService.getLocalizedText(record.value)
      };
    });
  }
  const propertyForDisplayText = AnnotationProcessor.getPropertyName(metaData?.datasource?.propertyForDisplayText);
  const propertyForValue = AnnotationProcessor.getPropertyName(metaData?.datasource?.propertyForValue);
  const parameters = Array.isArray(metaData?.datasource?.parameters) ? metaData?.datasource?.parameters : [];
  /**
   * This contains all the dependent
   */
  const currentContextName = c11nEnv.getContextName();
  const parameterValues: string[] = [];
  parameters.forEach((parameter: Parameter) => {
    if (AnnotationProcessor.isProperty(parameter?.value)) {
      let resolvedValue = getValue(
        `.${Utils.getFullPropertyReference([
          parentPropertyName,
          AnnotationProcessor.getPropertyName(parameter.value)
        ])}`,
        pageReference,
        currentContextName
      );
      parameterValues.push(
        Utils.getFullPropertyReference([
          pageReference,
          parentPropertyName,
          AnnotationProcessor.getPropertyName(parameter?.value)
        ])
      );

      if (resolvedValue === undefined || resolvedValue === null || resolvedValue === '') {
        resolvedValue = Constants.NULL_STRING;
      }
      parameterValueArray.push(resolvedValue);
    } else {
      // Add constant value as it is to get proper pxResults generated with correct hash.
      parameterValueArray.push(parameter?.value || Constants.NULL_STRING);
    }
  });

  if (currentContextName) {
    const currentContextObject = DatapageDependencyMap.getContextObjectByName(currentContextName) || {};
    parameterValues.forEach((val: string) => {
      if (val in currentContextObject) {
        const existingDependentProperties = currentContextObject[val];
        currentContextObject[val] = [...new Set([...existingDependentProperties, propertyName])];
      } else {
        currentContextObject[val] = [propertyName];
      }
    });
    DatapageDependencyMap.addContextName(currentContextName, currentContextObject);
  }
  const pxResults = getResultsFromDataPage(c11nEnv, parameterValueArray, dataPageName, isSharedDataPage);
  const l10nService = c11nEnv.getLocalizationService();
  const dpTranslation = (input: string) => {
    return l10nService.getLocalizedDataPage(input, dataPageName, propertyForDisplayText);
  };
  return pxResults.map((val: { [key: string]: unknown }) => {
    let displayValue = val[propertyForDisplayText] || val[`.${propertyForDisplayText}`];
    // if displayValue is string type then translate it
    if (typeof displayValue === 'string') {
      displayValue = dpTranslation(displayValue);
    }
    return {
      key: val[propertyForValue] || val[`.${propertyForValue}`],
      value: displayValue
    };
  });
});
